import { ReactNode, useId, useState } from 'react'
import type { MeanInterval, RateInterval } from '../../types'
import { TRACK_RECORD_GLOSSARY as GLOSSARY } from '../../config/glossary'
import { formatPnlPct } from '../../utils/pnl'
import { MIN_WIN_RATE_SAMPLE, formatWinRate, hasWinRate } from '../../utils/winRate'
import Tooltip from '../Tooltip'
import TerminalPanel from '../dashboard/TerminalPanel'
import { directionOf } from '../dashboard/chartTheme'

// Building blocks shared by the Track Record page's Signal engine and Paper views,
// so both records are shown the same honest way.

export const CLOSED_PAGE_SIZE = 20

/**
 * Newest closed signals shown above the "View all" expander; 0 collapses the
 * list entirely, like the excluded panel.
 */
export const PREVIEW_ROWS = 5

/** Fewest closes an interval can be taken from (backend: MIN_OBSERVATIONS). */
export const MIN_INTERVAL_SAMPLE = 2

export const PNL_TEXT = { up: 'text-green-400', down: 'text-red-400', flat: 'text-gray-300' } as const

export const formatR = (r: number) => `${r > 0 ? '+' : ''}${r.toFixed(2)}R`

export const pnlClass = (value: number) => PNL_TEXT[directionOf(value)]

/**
 * The bounds under a published estimate, or the reason there are none.
 *
 * Three states, never two: measured and clear of zero, measured and straddling
 * it, or not measured at all. The last is not "no edge" — it is "too few
 * closes to say" — and it says so rather than letting a bare number imply a
 * confidence it has not earned.
 */
export const IntervalNote = ({
  interval,
  format,
  testId,
  zeroTest = false,
  dense = false,
}: {
  interval: MeanInterval | RateInterval | null | undefined
  format: (value: number) => string
  testId: string
  /** Mean R only: a rate's interval is above zero whenever it has a win. */
  zeroTest?: boolean
  /** The dashboard's tighter rail: smaller type, shorter copy, same states. */
  dense?: boolean
}) => {
  if (!interval) return null
  const className = dense ? 'text-[11px] text-gray-500' : 'text-gray-500'

  if (!interval.measured || interval.low == null || interval.high == null) {
    const closes = `${interval.n} close${interval.n === 1 ? '' : 's'}`
    return (
      <p className={className} data-testid={testId} data-measured="false">
        <Tooltip content={GLOSSARY.interval}>
          {interval.n === 0
            ? `no interval yet${dense ? '' : ' · nothing closed'}`
            : `not measured · ${dense ? interval.n : closes} of ${MIN_INTERVAL_SAMPLE}`}
        </Tooltip>
      </p>
    )
  }

  const straddlesZero = zeroTest && (interval as MeanInterval).excludes_zero === false
  return (
    <p className={className} data-testid={testId} data-measured="true">
      <Tooltip content={GLOSSARY.interval}>
        <span className="font-mono tabular-nums">
          {Math.round(interval.confidence * 100)}% CI {format(interval.low)} to {format(interval.high)}
          {!dense && ` · n ${interval.n}`}
        </span>
      </Tooltip>
      {straddlesZero && <span className="text-amber-400/90"> · includes 0</span>}
    </p>
  )
}

export const StatTile = ({
  label,
  hint,
  value,
  valueClass = 'text-gray-50',
  testId,
  children,
}: {
  label: string
  hint?: string
  value: ReactNode
  valueClass?: string
  testId: string
  children?: ReactNode
}) => (
  <div className="rounded-lg border border-terminal-border bg-terminal-surface p-4" data-testid={testId}>
    <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
      {hint ? (
        <Tooltip content={hint} className="uppercase">
          {label}
        </Tooltip>
      ) : (
        label
      )}
    </p>
    <p className={`mt-1 font-mono text-2xl font-bold tabular-nums ${valueClass}`}>{value}</p>
    {children && <div className="mt-1 text-xs text-gray-400">{children}</div>}
  </div>
)

/** The rate never stands alone: its W/L count and closed total sit right under it. */
export const WinRateTile = ({
  winRate,
  closed,
  wins,
  losses,
  breakevens = 0,
  noun,
  interval,
}: {
  winRate: number | null
  closed: number
  wins: number
  losses: number
  breakevens?: number
  noun: string
  interval?: RateInterval | null
}) => (
  <StatTile label="Win rate" testId="stat-win-rate" value={formatWinRate(winRate, closed)}>
    <p className="font-mono tabular-nums">
      <span className="text-green-400">{wins}W</span>/<span className="text-red-400">{losses}L</span>
      {breakevens > 0 && (
        <>
          /<span className="text-gray-300">{breakevens}BE</span>
        </>
      )}
      <span>
        {' '}
        · {closed} {noun}
      </span>
    </p>
    {!hasWinRate(winRate, closed) && closed > 0 && (
      <p>{Math.max(MIN_WIN_RATE_SAMPLE - closed, 0)} more closes before a rate is quoted</p>
    )}
    {hasWinRate(winRate, closed) && (
      <IntervalNote
        interval={interval}
        format={(value) => `${value.toFixed(1)}%`}
        testId="stat-win-rate-interval"
      />
    )}
  </StatTile>
)

/**
 * Mean R, with the cost basis it was netted on and its confidence bounds.
 *
 * ``basis`` is required, and says out loud which costs came out. The engine and
 * the paper account model different venues at different rates, and this tile
 * renders both: without the basis a reader comparing the two records would be
 * comparing numbers netted differently under one label, which is how the
 * engine's gross R came to sit beside the paper account's net R as "Avg R".
 */
export const AvgRTile = ({
  avgR,
  basis,
  interval,
  children,
}: {
  avgR: number | null
  basis: string
  interval?: MeanInterval | null
  /** A further line under the interval (the engine's time in trade). */
  children?: ReactNode
}) => (
  <StatTile
    label="Avg R"
    hint={GLOSSARY.avgR}
    testId="stat-avg-r"
    value={avgR == null ? '—' : formatR(avgR)}
    valueClass={avgR == null ? 'text-gray-500' : pnlClass(avgR)}
  >
    <p data-testid="stat-avg-r-basis">{basis}</p>
    <IntervalNote interval={interval} format={formatR} testId="stat-avg-r-interval" zeroTest />
    {children}
  </StatTile>
)

const OUTCOME_BADGE = {
  win: 'bg-green-500/15 text-green-300 ring-green-500/30',
  loss: 'bg-red-500/15 text-red-300 ring-red-500/30',
  breakeven: 'bg-slate-500/15 text-gray-300 ring-slate-500/30',
} as const

export const OutcomeBadge = ({ win }: { win: boolean | null }) => {
  const word = win === true ? 'win' : win === false ? 'loss' : 'breakeven'
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold uppercase ring-1 ${OUTCOME_BADGE[word]}`}>
      {word}
    </span>
  )
}

export const DrawdownReadout = ({ value, unit }: { value: number; unit: 'points' | 'peak' }) => (
  <p className="text-right text-xs text-gray-400">
    <Tooltip content={unit === 'peak' ? GLOSSARY.maxDrawdownPeak : GLOSSARY.maxDrawdown}>Max drawdown</Tooltip>{' '}
    <span className="font-mono font-semibold tabular-nums text-red-400" data-testid="max-drawdown">
      {value > 0 ? formatPnlPct(-value) : '0.00%'}
    </span>
    {unit === 'peak' && <span className="text-gray-500"> from peak</span>}
  </p>
)

export const EmptyBox = ({ title, detail }: { title: string; detail?: ReactNode }) => (
  <div className="flex h-full flex-col items-center justify-center rounded-md border border-dashed border-terminal-border px-4 py-8 text-center">
    <p className="text-sm text-gray-300">{title}</p>
    {detail && <p className="mt-1 max-w-md text-xs text-gray-500">{detail}</p>}
  </div>
)

export const ErrorState = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
  <div className="rounded-lg border border-terminal-border bg-terminal-surface p-8 text-center">
    <p className="text-sm text-gray-300">{message}</p>
    <button
      type="button"
      onClick={onRetry}
      className="mt-3 rounded-md border border-terminal-border px-4 py-2 text-sm font-medium text-gray-200 hover:bg-terminal-raised"
    >
      Try again
    </button>
  </div>
)

export const LoadingState = ({ label }: { label: string }) => (
  <div className="rounded-lg border border-terminal-border bg-terminal-surface p-8 text-center text-sm text-gray-500">
    {label}
  </div>
)

/**
 * The closed-trades pager: "1–20 of 57 · page 1 of 3" and Newer / Older.
 * `page` is zero-based and already clamped; `shown` is the rows on this page.
 */
export const ClosedPager = ({
  page,
  pageCount,
  shown,
  total,
  onPage,
}: {
  page: number
  pageCount: number
  shown: number
  total: number
  onPage: (page: number) => void
}) => {
  const firstShown = page * CLOSED_PAGE_SIZE + 1
  const pagerButton =
    'rounded-md border border-terminal-border px-3 py-1.5 text-sm font-medium text-gray-200 hover:bg-terminal-raised disabled:cursor-not-allowed disabled:opacity-40'
  return (
    <nav
      aria-label="Closed trades pages"
      className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-terminal-border pt-4"
    >
      <p className="text-xs text-gray-500" data-testid="page-status">
        {firstShown}–{firstShown + shown - 1} of {total} · page {page + 1} of {pageCount}
      </p>
      <div className="flex gap-2">
        <button type="button" onClick={() => onPage(page - 1)} disabled={page === 0} className={pagerButton}>
          ← Newer
        </button>
        <button type="button" onClick={() => onPage(page + 1)} disabled={page >= pageCount - 1} className={pagerButton}>
          Older →
        </button>
      </div>
    </nav>
  )
}

export interface ClosedColumn<T> {
  header: string
  align?: 'right'
  render: (row: T) => ReactNode
  /** Extra classes for the cell, e.g. a P&L colour. */
  cellClass?: (row: T) => string
}

/**
 * Every closed trade, newest first, a page at a time: a table from md up and
 * one compact row per trade on phones.
 */
export const ClosedTradesPanel = <T,>({
  title,
  total,
  rows,
  rowKey,
  columns,
  mobileTitle,
  mobileDetail,
  win,
  empty,
  previewRows,
  note,
}: {
  title: string
  /** A line under the "N total · newest first" subtitle. */
  note?: ReactNode
  total: number
  /** Newest first. */
  rows: T[]
  rowKey: (row: T) => string | number
  columns: ClosedColumn<T>[]
  mobileTitle: (row: T) => ReactNode
  mobileDetail: (row: T) => ReactNode
  win: (row: T) => boolean | null
  empty: ReactNode
  /**
   * When set, only this many newest rows show until the viewer expands the full
   * paginated list. Presentation only: the rows and the paging are unchanged.
   */
  previewRows?: number
}) => {
  const [page, setPage] = useState(0)
  const [expanded, setExpanded] = useState(false)
  const listId = `closed-list-${useId().replace(/:/g, '')}`
  const collapsible = previewRows != null && rows.length > previewRows
  const collapsed = collapsible && !expanded
  const pageCount = Math.max(1, Math.ceil(rows.length / CLOSED_PAGE_SIZE))
  const currentPage = Math.min(page, pageCount - 1)
  const pageRows = collapsed
    ? rows.slice(0, previewRows)
    : rows.slice(currentPage * CLOSED_PAGE_SIZE, (currentPage + 1) * CLOSED_PAGE_SIZE)
  const toggle = collapsible ? (
    <button
      type="button"
      onClick={() => setExpanded((e) => !e)}
      aria-expanded={expanded}
      aria-controls={listId}
      className={`${pageRows.length > 0 ? 'mt-4 ' : ''}rounded-md border border-amber-500/40 px-3 py-1.5 text-sm font-medium text-amber-300 hover:bg-amber-500/10`}
      data-testid="closed-toggle"
    >
      {collapsed ? `View all ${rows.length} closed ${rows.length === 1 ? 'signal' : 'signals'} →` : 'Show fewer ↑'}
    </button>
  ) : null

  return (
    <TerminalPanel title={title} tone="slate" subtitle={
        <>
          <p className="text-xs text-gray-500">{total} total · newest first</p>
          {note && <p className="mt-0.5 text-xs text-gray-500" data-testid="closed-note">{note}</p>}
        </>
      }
    >
      {rows.length === 0 ? (
        empty
      ) : (
        <div id={listId}>
          {pageRows.length > 0 && (
            <>
              <ul className="divide-y divide-terminal-border md:hidden" data-testid="closed-list-mobile">
                {pageRows.map((row) => (
                  <li key={rowKey(row)} className="py-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="min-w-0 truncate text-sm">{mobileTitle(row)}</p>
                      <OutcomeBadge win={win(row)} />
                    </div>
                    <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 font-mono text-xs tabular-nums text-gray-400">
                      {mobileDetail(row)}
                    </div>
                  </li>
                ))}
              </ul>

              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full text-sm" data-testid="closed-table">
                  <thead>
                    <tr className="border-b border-terminal-border text-left text-[11px] uppercase tracking-wider text-gray-500">
                      {columns.map((column) => (
                        <th key={column.header} className={`py-2 pr-3 font-medium ${column.align === 'right' ? 'text-right' : ''}`}>
                          {column.header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-terminal-border">
                    {pageRows.map((row) => (
                      <tr key={rowKey(row)} data-testid="closed-row">
                        {columns.map((column) => (
                          <td
                            key={column.header}
                            className={`whitespace-nowrap py-2.5 pr-3 ${column.align === 'right' ? 'text-right font-mono tabular-nums' : ''} ${
                              column.cellClass?.(row) ?? ''
                            }`}
                          >
                            {column.render(row)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!collapsed && (
            <ClosedPager page={currentPage} pageCount={pageCount} shown={pageRows.length} total={rows.length} onPage={setPage} />
          )}
          {toggle}
        </div>
      )}
    </TerminalPanel>
  )
}
